const moneyFormat = (value, currency) => {
  if (!Number.isFinite(value)) return '';

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'GBP',
    }).format(value / 100);
  } catch (_error) {
    return `£${(value / 100).toFixed(2)}`;
  }
};

const DV_RECOMMENDATION_STATS_KEY = 'dvRecommendationStats';

const readRecommendationStats = () => {
  try {
    return JSON.parse(localStorage.getItem(DV_RECOMMENDATION_STATS_KEY) || '{}') || {};
  } catch (_error) {
    return {};
  }
};

const writeRecommendationStats = (stats) => {
  try {
    localStorage.setItem(DV_RECOMMENDATION_STATS_KEY, JSON.stringify(stats));
  } catch (_error) {
    // Recommendation ranking is best-effort when storage is unavailable.
  }
};

const recommendationProductId = (card) => card?.getAttribute('data-dv-product-id') || '';
const recommendationProductHandle = (card) => card?.getAttribute('data-dv-product-handle') || '';

const mergeRecommendationStats = (...sources) => sources.reduce((merged, source) => {
  if (!source || typeof source !== 'object') return merged;

  Object.entries(source).forEach(([productId, productStats]) => {
    if (!productStats || typeof productStats !== 'object') return;
    const currentStats = merged[productId] || { views: 0, clicks: 0 };
    merged[productId] = {
      views: Math.max(Number(currentStats.views) || 0, Number(productStats.views) || 0),
      clicks: Math.max(Number(currentStats.clicks) || 0, Number(productStats.clicks) || 0),
    };
  });

  return merged;
}, {});

const readSiteRecommendationStats = () => {
  if (window.dvRecommendationStats && typeof window.dvRecommendationStats === 'object') {
    return window.dvRecommendationStats;
  }
  return {};
};

const readMergedRecommendationStats = () => mergeRecommendationStats(readRecommendationStats(), readSiteRecommendationStats());

const recommendationScore = (stats, productId) => {
  const productStats = stats[productId] || {};
  return (Number(productStats.clicks) || 0) * 3 + (Number(productStats.views) || 0);
};

const sortRecommendationCards = (carousel, stats = readMergedRecommendationStats()) => {
  const track = carousel.querySelector('[data-dv-carousel-track]');
  if (!track) return;

  Array.from(track.querySelectorAll('[data-dv-recommendation-card]'))
    .map((card, index) => ({ card, index, productId: recommendationProductId(card) }))
    .sort((left, right) => {
      const scoreDiff = recommendationScore(stats, right.productId) - recommendationScore(stats, left.productId);
      return scoreDiff || left.index - right.index;
    })
    .forEach(({ card }) => track.appendChild(card));
};

const markRecommendationPreloaded = (card) => {
  card.setAttribute('data-dv-recommendation-preloaded', 'true');
  card.removeAttribute('data-dv-recommendation-preloading');
};

const preloadRecommendationImage = (card) => {
  const src = card?.getAttribute('data-dv-recommendation-preload-src');
  if (!src || card.hasAttribute('data-dv-recommendation-preloaded') || card.hasAttribute('data-dv-recommendation-preloading')) {
    return Promise.resolve();
  }

  card.setAttribute('data-dv-recommendation-preloading', 'true');

  return new Promise((resolve) => {
    const image = new Image();
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;

      if (typeof image.decode === 'function') {
        image.decode()
          .catch(() => {})
          .finally(() => {
            markRecommendationPreloaded(card);
            resolve();
          });
        return;
      }

      markRecommendationPreloaded(card);
      resolve();
    };

    image.decoding = 'async';
    image.loading = 'eager';
    image.onload = finish;
    image.onerror = () => {
      if (settled) return;
      settled = true;
      card.removeAttribute('data-dv-recommendation-preloading');
      resolve();
    };
    image.src = src;

    if (image.complete) {
      finish();
    }
  });
};

const preloadRecommendationImages = (carousel, startIndex = 0, batchSize = 8) => {
  const cards = Array.from(carousel.querySelectorAll('[data-dv-recommendation-card]'));
  const endIndex = Math.min(cards.length, startIndex + batchSize);

  cards.slice(startIndex, endIndex).forEach((card) => {
    preloadRecommendationImage(card);
  });
};

const scheduleRecommendationIdleWork = (callback) => {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(callback, { timeout: 900 });
    return;
  }

  window.setTimeout(() => callback({ timeRemaining: () => 12 }), 80);
};

const preloadAllRecommendationImages = (carousel, startIndex = 0, batchSize = 4) => {
  const cards = Array.from(carousel.querySelectorAll('[data-dv-recommendation-card]'));
  let index = Math.max(0, startIndex);

  const runBatch = (deadline) => {
    let loaded = 0;

    while (index < cards.length && loaded < batchSize) {
      const hasTime = !deadline || deadline.timeRemaining?.() > 4 || loaded === 0;
      if (!hasTime) break;

      preloadRecommendationImage(cards[index]);
      index += 1;
      loaded += 1;
    }

    if (index < cards.length) {
      scheduleRecommendationIdleWork(runBatch);
    }
  };

  scheduleRecommendationIdleWork(runBatch);
};

const preloadUpcomingRecommendationImages = (carousel) => {
  const track = carousel.querySelector('[data-dv-carousel-track]');
  if (!track) return;

  const cards = Array.from(track.querySelectorAll('[data-dv-recommendation-card]'));
  const preloadEdge = track.scrollLeft + (track.clientWidth * 3.5);
  const firstUpcomingIndex = cards.findIndex((card) => card.offsetLeft + card.clientWidth >= preloadEdge);
  preloadRecommendationImages(carousel, Math.max(firstUpcomingIndex, 0), 16);
};

const loadCarouselImages = (carousel) => {
  const images = Array.from(carousel.querySelectorAll('.dv-card img'));

  images.forEach((image, index) => {
    image.loading = 'eager';
    image.setAttribute('loading', 'eager');

    if (index >= 16) {
      if ('fetchPriority' in image) {
        image.fetchPriority = 'low';
      }
      image.setAttribute('fetchpriority', 'low');
    }

    if (image.hasAttribute('data-dv-carousel-image-requested')) return;
    image.setAttribute('data-dv-carousel-image-requested', 'true');

    const source = image.currentSrc || image.getAttribute('src');
    if (!source) return;

    const preloadImage = new Image();
    preloadImage.decoding = 'async';
    if (image.sizes) preloadImage.sizes = image.sizes;
    if (image.srcset) preloadImage.srcset = image.srcset;
    preloadImage.src = source;
  });
};

const isLocalRecommendationStatsEndpoint = (endpoint) => {
  if (!endpoint) return false;

  try {
    const url = new URL(endpoint, window.location.origin);
    return ['localhost', '127.0.0.1', '0.0.0.0'].includes(url.hostname);
  } catch (_error) {
    return false;
  }
};

const reportRecommendationEvent = (carousel, card, eventName) => {
  const endpoint = carousel?.getAttribute('data-dv-stats-endpoint');
  const detail = {
    productId: recommendationProductId(card),
    productHandle: recommendationProductHandle(card),
    eventName,
  };

  window.dispatchEvent(new CustomEvent('dv:recommendation-stats', { detail }));

  if (!endpoint || isLocalRecommendationStatsEndpoint(endpoint) || !detail.productId) return;

  const body = JSON.stringify(detail);
  if (navigator.sendBeacon) {
    navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
    return;
  }

  fetch(endpoint, {
    method: 'POST',
    body,
    keepalive: true,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
  }).catch(() => {});
};

const recordRecommendationEvent = (card, eventName) => {
  const carousel = card?.closest?.('[data-dv-choice-recommendations]');
  const productId = recommendationProductId(card);
  if (!productId) return;

  const stats = readRecommendationStats();
  const productStats = stats[productId] || { views: 0, clicks: 0 };
  productStats[eventName] = (Number(productStats[eventName]) || 0) + 1;
  stats[productId] = productStats;
  writeRecommendationStats(stats);
  reportRecommendationEvent(carousel, card, eventName);
};

const carouselElements = (root = document) => {
  const elements = [];

  if (root?.matches?.('[data-dv-carousel]')) {
    elements.push(root);
  }

  root?.querySelectorAll?.('[data-dv-carousel]').forEach((carousel) => elements.push(carousel));
  return elements;
};

const removeUnavailableCarouselStock = (root = document) => {
  root.querySelectorAll?.('[data-dv-carousel] [data-dv-card-root]').forEach((card) => {
    const quickAdd = card.querySelector('.dv-catalog-card__quick-add');
    const selectedUnavailable = card.querySelector('[data-dv-card-variant-option][data-dv-card-variant-available="false"]:checked');

    if (quickAdd?.disabled || selectedUnavailable) {
      card.remove();
    }
  });

  root.querySelectorAll?.('[data-dv-carousel] [data-dv-card-variant-option][data-dv-card-variant-available="false"]').forEach((input) => {
    input.closest('label')?.remove();
  });

  root.querySelectorAll?.('[data-dv-carousel] .dv-card-size-picker').forEach((picker) => {
    if (!picker.querySelector('[data-dv-card-variant-option]')) {
      picker.closest('[data-dv-card-root]')?.remove();
    }
  });
};

const removeCurrentRecommendationProduct = (root = document) => {
  root.querySelectorAll?.('[data-dv-choice-recommendations][data-dv-current-product-id]').forEach((carousel) => {
    const currentProductId = carousel.getAttribute('data-dv-current-product-id');
    if (!currentProductId) return;

    carousel.querySelectorAll('[data-dv-card-root][data-dv-product-id]').forEach((card) => {
      if (card.getAttribute('data-dv-product-id') === currentProductId) {
        card.remove();
      }
    });
  });
};

const initDvCarousel = (root = document) => {
  carouselElements(root).forEach((carousel) => {
    if (carousel.hasAttribute('data-dv-carousel-ready')) return;

    const track = carousel.querySelector('[data-dv-carousel-track]');
    const buttons = Array.from(carousel.querySelectorAll('[data-dv-carousel-button]'));
    if (!track || buttons.length === 0) return;

    carousel.setAttribute('data-dv-carousel-ready', 'true');
    track.style.scrollBehavior = 'auto';

    let targetScroll = track.scrollLeft;
    let isProgrammaticScroll = false;
    let programmaticScrollTimer;
    let isLoadingMore = false;

    const maxCarouselScroll = () => Math.max(track.scrollWidth - track.clientWidth, 0);
    const clampCarouselScroll = (value) => Math.min(Math.max(value, 0), maxCarouselScroll());
    const nextPagination = () => track.querySelector('[data-dv-carousel-pagination][data-dv-carousel-next-url]');
    const hasNextPage = () => Boolean(nextPagination());

    const loadMoreCarouselItems = async () => {
      const pagination = nextPagination();
      const nextUrl = pagination?.getAttribute('data-dv-carousel-next-url');

      if (!nextUrl || isLoadingMore) return false;

      isLoadingMore = true;
      carousel.setAttribute('data-dv-carousel-loading', 'true');

      try {
        const response = await fetch(nextUrl, {
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) return false;

        const template = document.createElement('template');
        template.innerHTML = (await response.text()).trim();

        pagination.remove();
        track.appendChild(template.content);
        removeUnavailableCarouselStock(carousel);
        removeCurrentRecommendationProduct(carousel);
        scheduleButtonUpdate();
        return true;
      } catch (_error) {
        return false;
      } finally {
        isLoadingMore = false;
        carousel.removeAttribute('data-dv-carousel-loading');
      }
    };

    const loadMoreIfNearEnd = () => {
      if (!hasNextPage()) return;

      const preloadEdge = track.scrollLeft + track.clientWidth;
      const threshold = track.scrollWidth - Math.max(track.clientWidth * 0.65, 240);

      if (preloadEdge >= threshold) {
        loadMoreCarouselItems();
      }
    };

    const updateButtons = () => {
      targetScroll = clampCarouselScroll(targetScroll);
      const maxScroll = maxCarouselScroll();
      const currentScroll = isProgrammaticScroll ? targetScroll : track.scrollLeft;

      buttons.forEach((button) => {
        const direction = button.getAttribute('data-dv-carousel-button');
        button.disabled = direction === 'prev'
          ? maxScroll <= 1 || currentScroll <= 1
          : (maxScroll <= 1 || currentScroll >= maxScroll - 1) && !hasNextPage();
      });
    };

    const scheduleButtonUpdate = () => {
      window.requestAnimationFrame(updateButtons);
    };

    buttons.forEach((button) => {
      button.addEventListener('click', async () => {
        const direction = button.getAttribute('data-dv-carousel-button') === 'next' ? 1 : -1;
        const shouldLoadMore = direction === 1 && targetScroll >= maxCarouselScroll() - 4;

        if (shouldLoadMore) {
          await loadMoreCarouselItems();
        }

        targetScroll = clampCarouselScroll(targetScroll + (direction * Math.max(track.clientWidth * 0.82, 240)));
        isProgrammaticScroll = true;
        window.clearTimeout(programmaticScrollTimer);
        track.scrollLeft = targetScroll;
        programmaticScrollTimer = window.setTimeout(() => {
          isProgrammaticScroll = false;
          targetScroll = track.scrollLeft;
          updateButtons();
        }, 80);
        updateButtons();
      });
    });

    track.addEventListener('scroll', () => {
      if (!isProgrammaticScroll) targetScroll = track.scrollLeft;
      loadMoreIfNearEnd();
      scheduleButtonUpdate();
    }, { passive: true });
    window.addEventListener('resize', () => {
      targetScroll = clampCarouselScroll(track.scrollLeft);
      scheduleButtonUpdate();
    });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(scheduleButtonUpdate);
      resizeObserver.observe(track);
    }

    if (carousel.getAttribute('data-dv-carousel-preload') === 'all') {
      loadCarouselImages(carousel);
    }

    updateButtons();
  });
};

const DV_THEME_CSS_VERSION = '20260612productmodule9';
const DV_FONT_STYLESHEET_URL = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Instrument+Sans:ital,wght@0,400..700;1,400..700&display=swap';

const dvInfoPageViews = {
  '/pages/customer-care': 'customer-care-pro',
  '/pages/delivery': 'delivery-pro',
  '/pages/returns': 'returns-pro',
  '/pages/faqs': 'faqs-pro',
  '/pages/care-guide': 'care-guide-pro',
  '/pages/contact': 'contact-pro',
  '/pages/about': 'about-pro',
  '/pages/our-story': 'our-story-pro',
  '/pages/craftsmanship': 'craftsmanship-pro',
  '/pages/sustainability': 'sustainability-pro',
};

const ensureCurrentThemeAssets = () => {
  const currentStylesheet = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
    .find((link) => link.href.includes('/assets/dv-theme.css'));

  if (currentStylesheet && !currentStylesheet.href.includes(DV_THEME_CSS_VERSION)) {
    const nextStylesheet = document.createElement('link');
    nextStylesheet.rel = 'stylesheet';
    nextStylesheet.href = `${currentStylesheet.href.split('?')[0]}?v=${DV_THEME_CSS_VERSION}`;
    document.head.append(nextStylesheet);
  }

  const hasCurrentFontPackage = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
    .some((link) => (
      link.href.includes('fonts.googleapis.com')
      && link.href.includes('Instrument+Sans')
      && link.href.includes('Cormorant+Garamond')
    ));

  if (hasCurrentFontPackage) return;

  const fontStylesheet = document.createElement('link');
  fontStylesheet.rel = 'stylesheet';
  fontStylesheet.href = DV_FONT_STYLESHEET_URL;
  document.head.append(fontStylesheet);
};

const refreshStaleInfoPage = async () => {
  const path = window.location.pathname.replace(/\/$/, '');
  const view = dvInfoPageViews[path];
  const currentInfoPage = document.querySelector('.dv-info-page');

  if (!view || !currentInfoPage || !currentInfoPage.querySelector('.dv-info-card-grid')) return;

  currentInfoPage.setAttribute('data-dv-info-refreshing', 'true');

  try {
    const url = new URL(window.location.href);
    url.searchParams.set('view', view);
    url.searchParams.set('dv-info-refresh', String(Date.now()));

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) return;

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const freshInfoPage = doc.querySelector('.dv-info-page');

    if (!freshInfoPage || freshInfoPage.querySelector('.dv-info-card-grid')) return;

    currentInfoPage.replaceWith(freshInfoPage);
  } catch (_error) {
    // Cached pages remain usable if the fresh view cannot be fetched.
  } finally {
    currentInfoPage.removeAttribute('data-dv-info-refreshing');
  }
};

const initInfoPageRecovery = () => {
  ensureCurrentThemeAssets();
  refreshStaleInfoPage();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initInfoPageRecovery, { once: true });
} else {
  initInfoPageRecovery();
}

removeUnavailableCarouselStock();
removeCurrentRecommendationProduct();
initDvCarousel();

document.addEventListener('shopify:section:load', (event) => {
  removeUnavailableCarouselStock(event.target);
  removeCurrentRecommendationProduct(event.target);
  initDvCarousel(event.target);
});

document.querySelectorAll('[data-dv-choice-recommendations]').forEach((carousel) => {
  const track = carousel.querySelector('[data-dv-carousel-track]');
  const viewed = new Set();

  sortRecommendationCards(carousel);
  const cards = Array.from(carousel.querySelectorAll('[data-dv-recommendation-card]'));
  preloadRecommendationImages(carousel, 0, 16);
  preloadAllRecommendationImages(carousel, 16, 4);

  const recordView = (card) => {
    const productId = recommendationProductId(card);
    if (!productId || viewed.has(productId)) return;
    viewed.add(productId);
    recordRecommendationEvent(card, 'views');
  };

  cards.forEach((card) => {
    card.querySelectorAll('[data-dv-recommendation-click]').forEach((link) => {
      link.addEventListener('click', () => recordRecommendationEvent(card, 'clicks'));
    });
  });

  if (track) {
    track.addEventListener('scroll', () => {
      window.requestAnimationFrame(() => preloadUpcomingRecommendationImages(carousel));
    }, { passive: true });
  }

  carousel.querySelectorAll('[data-dv-carousel-button]').forEach((button) => {
    const preloadAhead = () => preloadUpcomingRecommendationImages(carousel);
    button.addEventListener('pointerenter', preloadAhead);
    button.addEventListener('focus', preloadAhead);
    button.addEventListener('touchstart', preloadAhead, { passive: true });
    button.addEventListener('click', () => {
      window.setTimeout(() => preloadUpcomingRecommendationImages(carousel), 120);
    });
  });

  const statsEndpoint = carousel.getAttribute('data-dv-stats-endpoint');
  if (statsEndpoint && !isLocalRecommendationStatsEndpoint(statsEndpoint)) {
    fetch(statsEndpoint, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((siteStats) => {
        if (!siteStats) return;
        window.dvRecommendationStats = siteStats;
        sortRecommendationCards(carousel, mergeRecommendationStats(readRecommendationStats(), siteStats));
        preloadRecommendationImages(carousel, 0, 16);
        preloadAllRecommendationImages(carousel, 16, 4);
      })
      .catch(() => {});
  }

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          recordView(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, {
      root: track,
      threshold: 0.5,
    });

    cards.forEach((card) => observer.observe(card));
  } else {
    cards.forEach(recordView);
  }
});

document.querySelectorAll('[data-dv-gallery]').forEach((gallery) => {
  const slides = Array.from(gallery.querySelectorAll('[data-dv-gallery-slide]'));
  const thumbs = Array.from(gallery.querySelectorAll('[data-dv-gallery-thumb]'));
  const prev = gallery.querySelector('[data-dv-gallery-prev]');
  const next = gallery.querySelector('[data-dv-gallery-next]');
  const thumbRail = gallery.querySelector('[data-dv-gallery-thumbs]');
  const thumbPrev = gallery.querySelector('[data-dv-gallery-thumbs-prev]');
  const thumbNext = gallery.querySelector('[data-dv-gallery-thumbs-next]');
  let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

  const hydrateGallerySlide = (slide) => {
    const image = slide?.querySelector('img[data-dv-gallery-lazy-src]');
    if (!image) return;

    const lazySrc = image.getAttribute('data-dv-gallery-lazy-src');
    const lazySrcset = image.getAttribute('data-dv-gallery-lazy-srcset');

    if (lazySrcset) image.setAttribute('srcset', lazySrcset);
    if (lazySrc) image.setAttribute('src', lazySrc);

    image.removeAttribute('data-dv-gallery-lazy-src');
    image.removeAttribute('data-dv-gallery-lazy-srcset');
  };

  const updateThumbButtons = () => {
    if (!thumbRail || !thumbPrev || !thumbNext) return;
    const maxScroll = thumbRail.scrollWidth - thumbRail.clientWidth;
    const canScroll = maxScroll > 4;
    thumbPrev.hidden = !canScroll;
    thumbNext.hidden = !canScroll;
    if (!canScroll) return;
    thumbPrev.disabled = thumbRail.scrollLeft <= 4;
    thumbNext.disabled = thumbRail.scrollLeft >= maxScroll - 4;
  };

  const scrollThumbRail = (direction) => {
    if (!thumbRail) return;
    thumbRail.scrollBy({
      left: direction * Math.max(thumbRail.clientWidth * 0.7, 120),
      behavior: 'smooth',
    });
  };

  const scrollActiveThumbIntoRail = () => {
    const thumb = thumbs[active];
    if (!thumbRail || !thumb) return;

    const thumbLeft = thumb.offsetLeft;
    const thumbRight = thumbLeft + thumb.offsetWidth;
    const railLeft = thumbRail.scrollLeft;
    const railRight = railLeft + thumbRail.clientWidth;

    if (thumbLeft < railLeft) {
      thumbRail.scrollTo({ left: thumbLeft, behavior: 'smooth' });
    } else if (thumbRight > railRight) {
      thumbRail.scrollTo({ left: thumbRight - thumbRail.clientWidth, behavior: 'smooth' });
    }
  };

  const select = (index) => {
    if (!slides.length) return;
    active = (index + slides.length) % slides.length;
    hydrateGallerySlide(slides[active]);
    slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === active));
    thumbs.forEach((thumb, thumbIndex) => thumb.classList.toggle('is-active', thumbIndex === active));
    scrollActiveThumbIntoRail();
    updateThumbButtons();
  };

  const selectByMediaId = (mediaId) => {
    if (!mediaId) return false;
    const id = String(mediaId);
    const index = slides.findIndex((slide) => slide.getAttribute('data-dv-media-id') === id);
    if (index < 0) return false;
    select(index);
    return true;
  };

  const selectByImageSrc = (src) => {
    if (!src) return false;
    const cleanSrc = String(src).split('?')[0];
    const index = slides.findIndex((slide) => {
      const image = slide.querySelector('img');
      const imageSrc = image?.getAttribute('data-dv-gallery-lazy-src') || image?.currentSrc || image?.src || '';
      return image && String(imageSrc).split('?')[0] === cleanSrc;
    });
    if (index < 0) return false;
    select(index);
    return true;
  };

  gallery.dvSelectMedia = selectByMediaId;
  gallery.dvSelectImage = selectByImageSrc;

  thumbs.forEach((thumb, index) => {
    thumb.addEventListener('click', () => select(index));
  });

  prev?.addEventListener('click', () => select(active - 1));
  next?.addEventListener('click', () => select(active + 1));
  thumbPrev?.addEventListener('click', () => scrollThumbRail(-1));
  thumbNext?.addEventListener('click', () => scrollThumbRail(1));
  thumbRail?.addEventListener('scroll', updateThumbButtons, { passive: true });
  window.addEventListener('resize', updateThumbButtons);
  updateThumbButtons();
});

document.querySelectorAll('[data-dv-product-form]').forEach((form) => {
  const variantsScript = form.querySelector('[data-dv-product-variants]');
  const variantInput = form.querySelector('input[name="id"]');
  const priceNodes = document.querySelectorAll(`[data-dv-price="${form.dataset.productId}"]`);
  const addButton = form.querySelector('[data-dv-add]');
  const checkoutButton = form.querySelector('[data-dv-checkout-now]');
  const currency = form.getAttribute('data-currency') || 'GBP';
  const gallery = form.closest('.dv-product')?.querySelector('[data-dv-gallery]');
  const initialVariantId = variantInput?.value;
  let variants = [];

  try {
    variants = JSON.parse(variantsScript?.textContent || '[]');
  } catch (_error) {
    variants = [];
  }

  const selectedOptions = () =>
    Array.from(form.querySelectorAll('[data-dv-option]:checked'))
      .sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position))
      .map((input) => input.value);

  const optionPositions = () =>
    Array.from(new Set(
      Array.from(form.querySelectorAll('[data-dv-option]'))
        .map((input) => Number(input.dataset.position))
        .filter(Boolean),
    )).sort((a, b) => a - b);

  const selectedValuesByPosition = () => {
    const selected = {};
    form.querySelectorAll('[data-dv-option]:checked').forEach((input) => {
      selected[Number(input.dataset.position)] = {
        value: input.value,
        optionName: input.dataset.optionName || '',
      };
    });
    return selected;
  };

  const variantIdFromHash = () => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return '';
    const params = new URLSearchParams(hash);
    return params.get('variant') || '';
  };

  const selectVariantById = (variantId) => {
    if (!variantId) return false;
    const variant = variants.find((item) => String(item.id) === String(variantId));
    if (!variant) return false;

    variant.options.forEach((value, index) => {
      const position = index + 1;
      const optionInput = Array.from(form.querySelectorAll(`[data-dv-option][data-position="${position}"]`))
        .find((input) => input.value === value);
      if (optionInput) optionInput.checked = true;
    });

    if (variantInput) variantInput.value = variant.id;
    return true;
  };

  const isVisualOptionName = (name = '') => /\b(colou?r|design|pattern|style|print)\b/i.test(name);
  const isSizeOptionName = (name = '') => /\b(size|specification)\b/i.test(name);

  const variantMatchesSelections = (variant, selections, ignoredPosition = 0, options = {}) =>
    Object.entries(selections).every(([position, selection]) => {
      const optionPosition = Number(position);
      if (optionPosition === ignoredPosition) return true;
      if (options.ignoreVisualSelections && isVisualOptionName(selection.optionName)) return true;
      return variant.options[optionPosition - 1] === selection.value;
    });

  const variantForSelections = (selections, options = {}) =>
    variants.find((item) => (
      (options.includeUnavailable || item.available)
      && item.options.every((option, index) => option === selections[index])
    ));

  const variantExistsForSelections = (selections, options = {}) =>
    Boolean(variantForSelections(selections, options));

  const optionHasCompatibleVariant = (input, selections) => {
    const position = Number(input.dataset.position);
    const ignoresVisualSelections = isSizeOptionName(input.dataset.optionName || '');
    return variants.some((variant) => (
      variant.available
      && variant.options[position - 1] === input.value
      && variantMatchesSelections(variant, selections, position, {
        ignoreVisualSelections: ignoresVisualSelections,
      })
    ));
  };

  const refreshOptionAvailability = (options = {}) => {
    const selections = selectedValuesByPosition();
    form.querySelectorAll('[data-dv-option]').forEach((input) => {
      const label = input.closest('.dv-option');
      const hasCompatibleVariant = optionHasCompatibleVariant(input, selections);
      const preserveSelectedInput = options.preserveSelectedVariant && input.checked;
      const shouldHide = !hasCompatibleVariant && !preserveSelectedInput;
      input.disabled = shouldHide;
      input.toggleAttribute('aria-disabled', !hasCompatibleVariant);
      if (label) {
        label.hidden = shouldHide;
        label.style.display = shouldHide ? 'none' : '';
        label.classList.toggle('is-unavailable', !hasCompatibleVariant);
      }
    });
  };

  const ensureValidSelections = (options = {}) => {
    if (
      options.preserveSelectedVariant
      && String(variantInput?.value || '') === String(initialVariantId || '')
      && variantExistsForSelections(selectedOptions(), { includeUnavailable: true })
    ) {
      return false;
    }

    let changed = false;
    for (const position of optionPositions()) {
      const checked = form.querySelector(`[data-dv-option][data-position="${position}"]:checked`);
      if (checked && !checked.disabled && !checked.closest('.dv-option')?.hidden) {
        continue;
      }
      const nextInput = form.querySelector(`[data-dv-option][data-position="${position}"]:not(:disabled)`);
      if (nextInput) {
        nextInput.checked = true;
        changed = true;
      }
    }
    return changed;
  };

  const reconcileOptionAvailability = (options = {}) => {
    for (let attempts = 0; attempts < 4; attempts += 1) {
      refreshOptionAvailability(options);
      if (!ensureValidSelections(options)) break;
    }
    refreshOptionAvailability(options);
  };

  const updateVariant = (options = {}) => {
    if (!variants.length || !variantInput) return;
    reconcileOptionAvailability(options);
    const selected = selectedOptions();
    const variant = variantForSelections(selected, { includeUnavailable: true });
    if (!variant) return;

    variantInput.value = variant.id;
    form.querySelectorAll('[data-dv-option-label]').forEach((label) => {
      const position = label.getAttribute('data-dv-option-label');
      const optionName = label.getAttribute('data-option-name') || `Option ${position}`;
      const isVisualOption = label.getAttribute('data-option-visual') === 'true';
      const selectedInput = form.querySelector(`[data-dv-option][data-position="${position}"]:checked`);
      if (selectedInput) {
        const selectedDisplayValue = selectedInput.dataset.dvOptionDisplayValue || selectedInput.value;
        const selectedValueNode = label.querySelector('[data-dv-option-label-value]');
        if (selectedValueNode) {
          selectedValueNode.textContent = selectedDisplayValue;
        } else {
          label.textContent = `${isVisualOption ? 'Colour / Design' : optionName}: ${selectedDisplayValue}`;
        }
      }
    });

    priceNodes.forEach((node) => {
      node.textContent = moneyFormat(variant.price, currency);
    });

    if (addButton) {
      addButton.disabled = !variant.available;
      addButton.querySelector('[data-dv-add-text]').textContent = variant.available ? 'Add to Bag' : 'Sold Out';
    }
    if (checkoutButton) {
      checkoutButton.disabled = !variant.available;
    }

    const mediaId = variant.featured_media?.id || variant.featured_media_id;
    const selectedMedia = gallery?.dvSelectMedia?.(mediaId);
    if (!selectedMedia) {
      gallery?.dvSelectImage?.(variant.featured_image?.src);
    }

    if (options.updateUrl !== false && window.history?.replaceState && variant.id) {
      const url = new URL(window.location.href);
      url.searchParams.delete('variant');
      url.hash = `variant=${variant.id}`;
      window.history.replaceState({}, '', url);
    }
  };

  form.querySelectorAll('[data-dv-option]').forEach((input) => {
    input.addEventListener('change', updateVariant);
  });

  const hashVariantSelected = selectVariantById(variantIdFromHash());
  updateVariant({ updateUrl: false, preserveSelectedVariant: !hashVariantSelected });

  form.querySelectorAll('[data-dv-qty]').forEach((button) => {
    button.addEventListener('click', () => {
      const input = form.querySelector('input[name="quantity"]');
      if (!input) return;
      const direction = button.getAttribute('data-dv-qty') === 'plus' ? 1 : -1;
      const nextValue = Math.max(1, Number(input.value || 1) + direction);
      input.value = String(nextValue);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  checkoutButton?.addEventListener('click', async () => {
    if (checkoutButton.disabled) return;

    const previousText = checkoutButton.textContent;
    checkoutButton.disabled = true;
    checkoutButton.textContent = 'Opening Checkout...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) throw new Error('Unable to start checkout');
      window.location.href = '/checkout';
    } catch (_error) {
      checkoutButton.disabled = false;
      checkoutButton.textContent = previousText || 'Checkout Now';
    }
  });
});

const updateCardVariantPrice = (form, selectedOption) => {
  const card = form.closest('[data-dv-card-root]');
  const priceContainer = card?.querySelector('[data-dv-card-price]');
  const variantPrice = selectedOption.dataset.dvCardVariantPrice;

  if (!priceContainer || !variantPrice) return;

  const isSale = selectedOption.dataset.dvCardVariantOnSale === 'true';
  const compareAtPrice = selectedOption.dataset.dvCardVariantCompareAt;
  const saving = selectedOption.dataset.dvCardVariantSaving;
  const savingClass = priceContainer.classList.contains('dv-card__price')
    ? 'dv-card__saving'
    : 'dv-catalog-card__saving';

  priceContainer.replaceChildren();

  const price = document.createElement('span');
  price.className = isSale ? 'price price--sale' : 'price';
  price.textContent = variantPrice;
  priceContainer.append(price);

  if (isSale && compareAtPrice) {
    const compareAt = document.createElement('s');
    compareAt.className = 'compare-at-price';
    compareAt.textContent = compareAtPrice;
    priceContainer.append(compareAt);
  }

  if (isSale && saving) {
    const savingText = document.createElement('span');
    savingText.className = savingClass;
    savingText.textContent = `Save ${saving}`;
    priceContainer.append(savingText);
  }
};

document.addEventListener('change', (event) => {
  const selectedOption = event.target.closest?.('[data-dv-card-variant-option]');
  if (!selectedOption) return;

  const form = selectedOption.closest('.dv-catalog-card__form');
  if (!form) return;

  const selectedVariantId = selectedOption.dataset.dvCardVariantId || selectedOption.value;
  const variantInput = form.querySelector('input[name="id"]');
  const button = form.querySelector('.dv-catalog-card__quick-add');
  const available = selectedOption.dataset.dvCardVariantAvailable !== 'false' && !selectedOption.disabled;

  if (variantInput && selectedVariantId) {
    variantInput.value = selectedVariantId;
  }

  if (button) {
    button.disabled = !available;
    button.textContent = available ? 'Add to Bag' : 'Sold Out';
  }

  const card = form.closest('[data-dv-card-root]');
  if (card && selectedVariantId) {
    card.setAttribute('data-dv-variant-id', selectedVariantId);
  }

  updateCardVariantPrice(form, selectedOption);
});

document.addEventListener('click', (event) => {
  const quantityButton = event.target.closest?.('[data-dv-card-qty]');
  if (!quantityButton) return;

  const form = quantityButton.closest('.dv-catalog-card__form');
  const quantityInput = form?.querySelector('.dv-catalog-card__quantity-input');
  if (!quantityInput) return;

  const direction = quantityButton.getAttribute('data-dv-card-qty') === 'plus' ? 1 : -1;
  const min = Number(quantityInput.getAttribute('min') || 1);
  const current = Number(quantityInput.value || min);
  const nextValue = Math.max(min, current + direction);
  quantityInput.value = String(nextValue);
  quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
});

document.addEventListener('change', (event) => {
  const quantityInput = event.target.closest?.('.dv-catalog-card__quantity-input');
  if (!quantityInput) return;

  const min = Number(quantityInput.getAttribute('min') || 1);
  const value = Number(quantityInput.value || min);
  quantityInput.value = String(Math.max(min, Math.floor(value || min)));
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest?.('.dv-catalog-card__form');
  if (!form) return;

  event.preventDefault();

  const button = form.querySelector('.dv-catalog-card__quick-add');
  if (!button || button.disabled) return;

  const previousText = button.textContent;
  button.disabled = true;
  button.textContent = 'Adding...';

  try {
    const quantityInput = form.querySelector('.dv-catalog-card__quantity-input');
    if (quantityInput) {
      const min = Number(quantityInput.getAttribute('min') || 1);
      const value = Number(quantityInput.value || min);
      quantityInput.value = String(Math.max(min, Math.floor(value || min)));
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: new FormData(form),
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) throw new Error('Unable to add item');

    button.textContent = 'Added';

    try {
      const cartResponse = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        document.querySelectorAll('.dv-cart-count').forEach((count) => {
          count.textContent = String(cart.item_count || 0);
        });
      }
    } catch (_error) {
      // Cart-count refresh is best-effort; the item has already been added.
    }

    window.setTimeout(() => {
      button.disabled = false;
      button.textContent = previousText || 'Add to Bag';
    }, 2200);
  } catch (_error) {
    button.textContent = 'Try Again';
    window.setTimeout(() => {
      button.disabled = false;
      button.textContent = previousText || 'Add to Bag';
    }, 2200);
  }
});

const LOCAL_CATALOG_FILTER_TYPES = ['tier', 'product-type', 'material', 'size'];

const createLocalCatalogFilterMap = () => ({
  tier: new Set(),
  'product-type': new Set(),
  material: new Set(),
  size: new Set(),
});

const catalogItems = () => [...document.querySelectorAll('.product-grid__item[data-dv-local-filter-card]')];

const itemLocalFilterValues = (item, type) => {
  const rawValue = item.getAttribute(`data-dv-${type}`) || '';

  return rawValue
    .split(/\s+/)
    .map((value) => value.trim())
    .filter(Boolean);
};

const refreshLocalCatalogFilterOptions = () => {
  const availableByType = createLocalCatalogFilterMap();

  catalogItems().forEach((item) => {
    LOCAL_CATALOG_FILTER_TYPES.forEach((type) => {
      itemLocalFilterValues(item, type).forEach((value) => {
        availableByType[type].add(value);
      });
    });
  });

  document.querySelectorAll('[data-dv-local-filter-option]').forEach((option) => {
    const type = option.getAttribute('data-dv-local-filter-option');
    const value = option.getAttribute('data-dv-local-filter-value') || '';
    const isAvailable = Boolean(availableByType[type]?.has(value));
    option.hidden = !isAvailable;

    const input = option.querySelector('.dv-local-filter__input');
    if (input) {
      input.disabled = !isAvailable;
      if (!isAvailable) input.checked = false;
    }
  });

  document.querySelectorAll('[data-dv-local-filter-panel]').forEach((panel) => {
    const type = panel.getAttribute('data-dv-local-filter-panel');
    panel.hidden = !availableByType[type]?.size;
  });
};

const applyLocalCatalogFilters = () => {
  const selectedValues = createLocalCatalogFilterMap();

  document.querySelectorAll('.dv-local-filter__input:checked').forEach((input) => {
    const type = input.getAttribute('data-dv-local-filter');
    if (selectedValues[type]) selectedValues[type].add(input.value);
  });

  catalogItems().forEach((item) => {
    const matchesAllFilters = LOCAL_CATALOG_FILTER_TYPES.every((type) => {
      const selected = type === 'size' ? selectedValues.size : selectedValues[type];
      if (!selected.size) return true;

      return itemLocalFilterValues(item, type).some((value) => selected.has(value));
    });

    item.hidden = !matchesAllFilters;
  });
};

document.addEventListener('DOMContentLoaded', () => {
  refreshLocalCatalogFilterOptions();
  applyLocalCatalogFilters();

  const collectionRoot = document.querySelector('results-list') || document.body;
  let refreshTimer;
  const observer = new MutationObserver(() => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => {
      refreshLocalCatalogFilterOptions();
      applyLocalCatalogFilters();
    }, 50);
  });
  observer.observe(collectionRoot, { childList: true, subtree: true });
});

document.addEventListener('change', (event) => {
  if (event.target.closest?.('.dv-local-filter__input')) {
    applyLocalCatalogFilters();
  }
});

document.addEventListener('input', (event) => {
  const slider = event.target.closest?.('.dv-price-slider__range');
  if (!slider) return;

  const wrapper = slider.closest('[data-dv-price-slider]');
  const priceFacet = slider.closest('price-facet-component');
  const minSlider = wrapper?.querySelector('[data-dv-price-slider-min]');
  const maxSlider = wrapper?.querySelector('[data-dv-price-slider-max]');
  const minInput = priceFacet?.querySelector('[ref="minInput"]');
  const maxInput = priceFacet?.querySelector('[ref="maxInput"]');
  if (!minSlider || !maxSlider || !minInput || !maxInput) return;

  let minValue = Number(minSlider.value || 0);
  let maxValue = Number(maxSlider.value || 0);

  if (minValue > maxValue) {
    if (slider === minSlider) {
      maxValue = minValue;
      maxSlider.value = String(maxValue);
    } else {
      minValue = maxValue;
      minSlider.value = String(minValue);
    }
  }

  minInput.value = minValue > 0 ? String(minValue) : '';
  maxInput.value = maxValue < Number(maxSlider.max) ? String(maxValue) : '';
});

document.addEventListener('change', (event) => {
  const slider = event.target.closest?.('.dv-price-slider__range');
  if (!slider) return;

  const priceFacet = slider.closest('price-facet-component');
  const input = slider.matches('[data-dv-price-slider-min]')
    ? priceFacet?.querySelector('[ref="minInput"]')
    : priceFacet?.querySelector('[ref="maxInput"]');
  input?.dispatchEvent(new Event('change', { bubbles: true }));
});
